import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, History, Save, Trash2, Upload } from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  Checkbox,
  EmptyState,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  Spinner,
  Textarea,
} from '@/components/ui';
import { getErrorMessage } from '@/services/apiClient';
import { toast } from '@/store/toastStore';
import {
  deleteHealthHistoryFiles,
  downloadHealthHistoryFile,
  getHealthHistory,
  uploadHealthHistoryFile,
  upsertHealthHistory,
  type HealthHistory,
  type MedicalClearanceField,
  type MedicalClearanceFile,
  type MedicalClearanceHistoryEntry,
  type MedicalClearanceSection,
  type UpsertHealthHistoryPayload,
  type VitalsHistoryRow,
} from '@/services/patients.service';
import type { SelectOption } from '@/types/common.types';

interface FormState {
  healthFields: Record<string, string>;
  recordFields: Record<number, string>;
}

function buildFormState(data: HealthHistory): FormState {
  const healthFields: Record<string, string> = {};
  const recordFields: Record<number, string> = {};

  for (const section of data.sections) {
    for (const field of section.fields) {
      if (field.kind === 'health' && field.questionKey) {
        healthFields[field.questionKey] = field.value ?? '';
      }

      if (field.kind === 'record' && field.recordFieldId) {
        recordFields[field.recordFieldId] = field.value ?? '';
      }
    }
  }

  return { healthFields, recordFields };
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return 'Unknown';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
}

function attributionLabel(entry: MedicalClearanceHistoryEntry): string | null {
  const id = entry.userId;
  if (typeof id === 'string' && id.toUpperCase() === 'P') {
    return 'Patient';
  }
  if (entry.userName && entry.userName.trim() !== '') {
    return entry.userName;
  }
  return null;
}

function computeBmi(form: FormState): string {
  const feet = Number(form.healthFields.height_feet ?? '');
  const inches = Number(form.healthFields.height_inches ?? '');
  const weight = Number(form.healthFields.weight ?? '');

  if (!Number.isFinite(feet) || !Number.isFinite(inches) || !Number.isFinite(weight)) {
    return 'N/A';
  }

  const totalInches = feet * 12 + inches;
  if (totalInches <= 0 || weight <= 0) {
    return 'N/A';
  }

  return ((weight / (totalInches * totalInches)) * 703).toFixed(1);
}

function getFieldValue(form: FormState, field: MedicalClearanceField): string {
  if (field.kind === 'health') {
    return form.healthFields[field.questionKey ?? field.fieldId] ?? '';
  }

  return form.recordFields[field.recordFieldId ?? -1] ?? '';
}

function setFieldValue(form: FormState, field: MedicalClearanceField, value: string): FormState {
  if (field.kind === 'health') {
    return {
      ...form,
      healthFields: {
        ...form.healthFields,
        [field.questionKey ?? field.fieldId]: value,
      },
    };
  }

  return {
    ...form,
    recordFields: {
      ...form.recordFields,
      [field.recordFieldId ?? -1]: value,
    },
  };
}

function setHealthValue(form: FormState, key: string, value: string): FormState {
  return {
    ...form,
    healthFields: { ...form.healthFields, [key]: value },
  };
}

function buildPayload(form: FormState): UpsertHealthHistoryPayload {
  return {
    healthFields: { ...form.healthFields },
    recordFields: Object.entries(form.recordFields).map(([recordFieldId, value]) => ({
      recordFieldId: Number(recordFieldId),
      value,
    })),
  };
}

function HistoryPopover({
  fields,
  onApply,
  disabled,
}: {
  fields: MedicalClearanceField[];
  onApply: (field: MedicalClearanceField, value: string) => void;
  disabled?: boolean;
}) {
  const fieldsWithHistory = fields.filter((f) => f.history.length > 0);
  if (fieldsWithHistory.length === 0) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="xs" leftIcon={<History className="h-3.5 w-3.5" />}>
          <span className="hidden @[10rem]:inline">History</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-80 max-h-72 overflow-y-auto p-3">
        {fieldsWithHistory.map((field) => (
          <div key={field.fieldId} className="space-y-1.5">
            {fieldsWithHistory.length > 1 && (
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 pt-2 first:pt-0">
                {field.label}
              </p>
            )}
            {field.history.map((entry, index) => {
              const author = attributionLabel(entry);
              const interactive = !disabled;
              return (
                <button
                  key={`${field.fieldId}-${entry.createdAt ?? 'unknown'}-${index}`}
                  type="button"
                  disabled={!interactive}
                  onClick={interactive ? () => onApply(field, entry.value) : undefined}
                  className={`block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-left ${
                    interactive
                      ? 'hover:border-blue-300 hover:bg-blue-50'
                      : 'cursor-default opacity-90'
                  }`}
                >
                  <div className="text-sm font-medium text-slate-800">
                    {entry.displayValue.trim() !== '' ? entry.displayValue : '(Blank value)'}
                  </div>
                  <div className="text-xs text-slate-500">
                    {formatDateTime(entry.createdAt)}
                    {author ? ` by ${author}` : ''}
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function FieldRenderer({
  field,
  form,
  setForm,
  onApplyHistory,
  readOnly,
}: {
  field: MedicalClearanceField;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState | null>>;
  onApplyHistory: (field: MedicalClearanceField, value: string) => void;
  readOnly: boolean;
}) {
  const value = getFieldValue(form, field);
  const options: SelectOption<string>[] = field.options.map((option) => ({
    value: String(option.id),
    label: option.label,
  }));

  const setValue = (nextValue: string) => {
    if (readOnly) return;
    setForm((current) => (current ? setFieldValue(current, field, nextValue) : current));
  };

  const header = (
    <div className="mb-2 flex items-center justify-between gap-2">
      <p className="text-sm font-medium text-slate-700">{field.label}</p>
      <HistoryPopover fields={[field]} onApply={onApplyHistory} disabled={readOnly} />
    </div>
  );

  if (field.type === 'textarea') {
    return (
      <div>
        {header}
        <Textarea
          value={value}
          rows={field.rows ?? 3}
          disabled={readOnly}
          onChange={(e) => setValue(e.target.value)}
        />
      </div>
    );
  }

  if (field.type === 'dropdown') {
    return (
      <div>
        {header}
        <Select
          value={value}
          placeholder="Select one..."
          disabled={readOnly}
          options={[{ value: '', label: 'Select one...' }, ...options]}
          onChange={(nextValue) => setValue(nextValue ?? '')}
        />
      </div>
    );
  }

  if (field.type === 'checkbox') {
    const selectedValues = new Set(
      value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item !== ''),
    );

    return (
      <div>
        {header}
        <div className="space-y-2 rounded-md border border-slate-200 bg-white p-3">
          {options.map((option) => (
            <Checkbox
              key={`${field.fieldId}-${option.value}`}
              label={option.label}
              checked={selectedValues.has(option.value)}
              disabled={readOnly}
              onChange={(e) => {
                const next = new Set(selectedValues);
                if ((e.target as HTMLInputElement).checked) {
                  next.add(option.value);
                } else {
                  next.delete(option.value);
                }
                setValue(Array.from(next).join(','));
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (field.type === 'radio') {
    return (
      <div>
        {header}
        <div className="space-y-2 rounded-md border border-slate-200 bg-white p-3">
          {options.map((option) => (
            <label
              key={`${field.fieldId}-${option.value}`}
              className={`flex items-center gap-2 text-sm text-slate-700 ${
                readOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
              }`}
            >
              <input
                type="radio"
                name={field.fieldId}
                checked={value === option.value}
                disabled={readOnly}
                onChange={() => setValue(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {header}
      <Input value={value} disabled={readOnly} onChange={(e) => setValue(e.target.value)} />
    </div>
  );
}

function VitalRow({
  label,
  control,
  toggle,
}: {
  label: string;
  control: React.ReactNode;
  toggle?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-2.5">
      <span className="text-sm font-medium leading-tight text-slate-600">{label}</span>
      <div className="flex min-w-0 shrink-0 items-center gap-1.5">
        {control}
        {toggle && <div className="shrink-0">{toggle}</div>}
      </div>
    </div>
  );
}

function VitalTile({ children }: { children: React.ReactNode }) {
  return (
    <div className="@container divide-y divide-slate-200/70 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-1">
      {children}
    </div>
  );
}

// Vitals are free-text `answer` rows in the DB, so the only thing keeping a
// letter out of a height or a temperature is what we filter on the way in.
// Height is whole feet/inches; the measured vitals accept one decimal point.
function sanitizeWholeNumber(value: string): string {
  return value.replace(/[^0-9]/g, '');
}

function sanitizeDecimalNumber(value: string): string {
  const cleaned = value.replace(/[^0-9.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) {
    return cleaned;
  }

  // Keep the first '.' and drop any later ones so "98..6" / "98.6.1" can't be typed.
  return `${cleaned.slice(0, firstDot + 1)}${cleaned.slice(firstDot + 1).replace(/\./g, '')}`;
}

function vitalInputClass(width: string): string {
  return `${width} h-9 rounded-md border border-slate-300 bg-white text-center text-sm text-slate-800 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100 disabled:text-slate-500`;
}

const VITAL_UNIT_CLASS = 'text-xs font-medium text-slate-500';

function VitalsSection({
  section,
  form,
  setForm,
  bmi,
  age,
  onApplyHistory,
  showVitalsTable,
  onToggleVitalsTable,
  readOnly,
}: {
  section: MedicalClearanceSection;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState | null>>;
  bmi: string;
  age: number | null | undefined;
  onApplyHistory: (field: MedicalClearanceField, value: string) => void;
  showVitalsTable: boolean;
  onToggleVitalsTable: () => void;
  readOnly: boolean;
}) {
  const fieldByKey = new Map<string, MedicalClearanceField>();
  for (const field of section.fields) {
    if (field.questionKey) {
      fieldByKey.set(field.questionKey, field);
    }
  }

  const setHealth = (key: string, nextValue: string) => {
    if (readOnly) return;
    setForm((current) => (current ? setHealthValue(current, key, nextValue) : current));
  };

  const heightFeet = fieldByKey.get('height_feet');
  const heightInches = fieldByKey.get('height_inches');
  const weight = fieldByKey.get('weight');
  const bpSys = fieldByKey.get('bp_systolic');
  const bpDia = fieldByKey.get('bp_diastolic');
  const heartRate = fieldByKey.get('heart_rate');
  const temperature = fieldByKey.get('temperature');

  const vitalsHistoryRows = section.vitalsHistory ?? [];

  const groupPopover = (...fields: (MedicalClearanceField | undefined)[]): React.ReactNode => {
    const valid = fields.filter(Boolean) as MedicalClearanceField[];
    return <HistoryPopover fields={valid} onApply={onApplyHistory} disabled={readOnly} />;
  };

  return (
    <Card>
      <CardBody className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-base font-semibold text-slate-900">{section.title}</h3>
            <span className="rounded-full bg-blue-50 px-3 py-0.5 text-xs font-semibold text-blue-700">
              BMI {bmi}
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-0.5 text-xs font-semibold text-slate-700">
              Age {age ?? 'N/A'}
            </span>
            {vitalsHistoryRows.length > 0 && (
              <Button
                variant="ghost"
                size="xs"
                leftIcon={<History className="h-3.5 w-3.5" />}
                onClick={onToggleVitalsTable}
                aria-pressed={showVitalsTable}
              >
                {showVitalsTable ? 'Hide History' : 'History'}
              </Button>
            )}
          </div>
          {section.lastUpdatedAt && (
            <p className="text-xs text-slate-500">
              Last update {formatDateTime(section.lastUpdatedAt)}
              {section.lastUpdatedBy ? ` by ${section.lastUpdatedBy}` : ''}
            </p>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <VitalTile>
            <VitalRow
              label="Height"
              toggle={groupPopover(heightFeet, heightInches)}
              control={
                <>
                  <input
                    aria-label="Height (feet)"
                    value={form.healthFields.height_feet ?? ''}
                    maxLength={1}
                    inputMode="numeric"
                    disabled={readOnly}
                    className={vitalInputClass('w-12')}
                    onChange={(e) => setHealth('height_feet', sanitizeWholeNumber(e.target.value))}
                  />
                  <span className={VITAL_UNIT_CLASS}>ft</span>
                  <input
                    aria-label="Height (inches)"
                    value={form.healthFields.height_inches ?? ''}
                    maxLength={2}
                    inputMode="numeric"
                    disabled={readOnly}
                    className={vitalInputClass('w-14')}
                    onChange={(e) => setHealth('height_inches', sanitizeWholeNumber(e.target.value))}
                  />
                  <span className={VITAL_UNIT_CLASS}>in</span>
                </>
              }
            />
            <VitalRow
              label="Weight"
              toggle={groupPopover(weight)}
              control={
                <>
                  <input
                    aria-label="Weight"
                    value={form.healthFields.weight ?? ''}
                    maxLength={6}
                    inputMode="decimal"
                    disabled={readOnly}
                    className={vitalInputClass('w-20')}
                    onChange={(e) => setHealth('weight', sanitizeDecimalNumber(e.target.value))}
                  />
                  <span className={VITAL_UNIT_CLASS}>lbs</span>
                </>
              }
            />
          </VitalTile>

          <VitalTile>
            <VitalRow
              label="Blood Pressure"
              toggle={groupPopover(bpSys, bpDia)}
              control={
                <>
                  <input
                    aria-label="Blood pressure (systolic)"
                    value={form.healthFields.bp_systolic ?? ''}
                    maxLength={5}
                    inputMode="decimal"
                    disabled={readOnly}
                    className={vitalInputClass('w-14')}
                    onChange={(e) => setHealth('bp_systolic', sanitizeDecimalNumber(e.target.value))}
                  />
                  <span className={VITAL_UNIT_CLASS}>/</span>
                  <input
                    aria-label="Blood pressure (diastolic)"
                    value={form.healthFields.bp_diastolic ?? ''}
                    maxLength={5}
                    inputMode="decimal"
                    disabled={readOnly}
                    className={vitalInputClass('w-14')}
                    onChange={(e) => setHealth('bp_diastolic', sanitizeDecimalNumber(e.target.value))}
                  />
                </>
              }
            />
            <VitalRow
              label="Heart Rate"
              toggle={groupPopover(heartRate)}
              control={
                <>
                  <input
                    aria-label="Heart rate"
                    value={form.healthFields.heart_rate ?? ''}
                    maxLength={3}
                    inputMode="numeric"
                    disabled={readOnly}
                    className={vitalInputClass('w-20')}
                    onChange={(e) => setHealth('heart_rate', sanitizeWholeNumber(e.target.value))}
                  />
                  <span className={VITAL_UNIT_CLASS}>bpm</span>
                </>
              }
            />
          </VitalTile>

          <VitalTile>
            <VitalRow
              label="Temperature"
              toggle={groupPopover(temperature)}
              control={
                <>
                  <input
                    aria-label="Temperature"
                    value={form.healthFields.temperature ?? ''}
                    maxLength={5}
                    inputMode="decimal"
                    disabled={readOnly}
                    className={vitalInputClass('w-20')}
                    onChange={(e) => setHealth('temperature', sanitizeDecimalNumber(e.target.value))}
                  />
                  <span className={VITAL_UNIT_CLASS}>°F</span>
                </>
              }
            />
          </VitalTile>
        </div>

        {showVitalsTable && vitalsHistoryRows.length > 0 && (
          <VitalsHistoryTable rows={vitalsHistoryRows} />
        )}
      </CardBody>
    </Card>
  );
}

function formatHeightCell(row: VitalsHistoryRow): string {
  const ftRaw = row.heightFeet?.trim() ?? '';
  const inRaw = row.heightInches?.trim() ?? '';
  const parts: string[] = [];
  if (ftRaw !== '' && !Number.isNaN(Number(ftRaw))) {
    parts.push(`${ftRaw}'`);
  }
  if (inRaw !== '' && !Number.isNaN(Number(inRaw))) {
    parts.push(`${inRaw}"`);
  }
  return parts.join(' ') || '—';
}

function VitalsHistoryTable({ rows }: { rows: VitalsHistoryRow[] }) {
  return (
    <div className="overflow-hidden rounded-md border border-slate-200">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">Date</th>
            <th className="px-3 py-2 text-left font-semibold">Height</th>
            <th className="px-3 py-2 text-left font-semibold">Weight (lb)</th>
            <th className="px-3 py-2 text-left font-semibold">Systolic</th>
            <th className="px-3 py-2 text-left font-semibold">Diastolic</th>
            <th className="px-3 py-2 text-left font-semibold">Heart Rate</th>
            <th className="px-3 py-2 text-left font-semibold">Temp (°F)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {rows.map((row) => (
            <tr key={row.date}>
              <td className="px-3 py-2 text-slate-700">{row.date}</td>
              <td className="px-3 py-2 text-slate-700">{formatHeightCell(row)}</td>
              <td className="px-3 py-2 text-slate-700">{row.weight ?? '—'}</td>
              <td className="px-3 py-2 text-slate-700">{row.bpSystolic ?? '—'}</td>
              <td className="px-3 py-2 text-slate-700">{row.bpDiastolic ?? '—'}</td>
              <td className="px-3 py-2 text-slate-700">{row.heartRate ?? '—'}</td>
              <td className="px-3 py-2 text-slate-700">{row.temperature ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GenericSection({
  section,
  form,
  setForm,
  onApplyHistory,
}: {
  section: MedicalClearanceSection;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState | null>>;
  onApplyHistory: (field: MedicalClearanceField, value: string) => void;
}) {
  return (
    <Card>
      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 pb-4">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-slate-900">{section.title}</h3>
            {section.readOnly && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                Read only
              </span>
            )}
          </div>
          {section.lastUpdatedAt && (
            <p className="text-xs text-slate-500">
              Last update {formatDateTime(section.lastUpdatedAt)}
              {section.lastUpdatedBy ? ` by ${section.lastUpdatedBy}` : ''}
            </p>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {section.fields.map((field) => {
            const isWide = field.type === 'textarea' || section.fields.length === 1;
            return (
              <div
                key={`${section.sectionId}-${field.fieldId}`}
                className={isWide ? 'md:col-span-2' : ''}
              >
                <FieldRenderer
                  field={field}
                  form={form}
                  setForm={setForm}
                  onApplyHistory={onApplyHistory}
                  readOnly={section.readOnly}
                />
              </div>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
}

function FilesSection({
  patientId,
  files,
  onUpload,
  onDelete,
}: {
  patientId: number;
  files: MedicalClearanceFile[];
  onUpload: (file: File) => void;
  onDelete: (fileName: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const handleDownload = async (fileName: string) => {
    setDownloading(fileName);
    try {
      const objectUrl = await downloadHealthHistoryFile(patientId, fileName);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) {
              return;
            }

            onUpload(file);
            e.target.value = '';
          }}
        />
        <Button
          variant="secondary"
          leftIcon={<Upload className="h-4 w-4" />}
          onClick={() => inputRef.current?.click()}
        >
          Upload File
        </Button>
      </div>

      {files.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          No medical clearance files uploaded yet.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {files.map((file) => (
            <div key={file.name} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{file.name}</p>
                <p className="text-xs text-slate-500">
                  {file.extension ? file.extension.toUpperCase() : 'FILE'} ·{' '}
                  {Math.max(1, Math.round(file.size / 1024))} KB
                </p>
                <p className="text-xs text-slate-500">Updated {formatDateTime(file.modifiedAt)}</p>
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Download className="h-4 w-4" />}
                  loading={downloading === file.name}
                  onClick={() => handleDownload(file.name)}
                >
                  Download
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Trash2 className="h-4 w-4" />}
                  onClick={() => onDelete(file.name)}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UploadSection({
  patientId,
  files,
  onUpload,
  onDelete,
  section,
}: {
  patientId: number;
  files: MedicalClearanceFile[];
  onUpload: (file: File) => void;
  onDelete: (fileName: string) => void;
  section: MedicalClearanceSection;
}) {
  return (
    <Card>
      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 pb-4">
          <h3 className="text-base font-semibold text-slate-900">{section.title}</h3>
        </div>
        <FilesSection
          patientId={patientId}
          files={files}
          onUpload={onUpload}
          onDelete={onDelete}
        />
      </CardBody>
    </Card>
  );
}

export function HealthHistoryTab({ patientId }: { patientId: number }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [showVitalsTable, setShowVitalsTable] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['health-history', patientId],
    queryFn: () => getHealthHistory(patientId),
  });

  useEffect(() => {
    if (data) {
      setForm(buildFormState(data));
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: UpsertHealthHistoryPayload) => upsertHealthHistory(patientId, payload),
    onSuccess: async () => {
      setSaveError(null);
      toast.success('Health information saved');
      await queryClient.invalidateQueries({ queryKey: ['health-history', patientId] });
    },
    onError: (mutationError) => {
      const message = getErrorMessage(mutationError, 'Failed to save medical clearance.');
      setSaveError(message);
      toast.error('Failed to save health information', message);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadHealthHistoryFile(patientId, file),
    onSuccess: async () => {
      setFileError(null);
      await queryClient.invalidateQueries({ queryKey: ['health-history', patientId] });
    },
    onError: (mutationError) => {
      setFileError(getErrorMessage(mutationError, 'Failed to upload file.'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (fileName: string) => deleteHealthHistoryFiles(patientId, [fileName]),
    onSuccess: async () => {
      setFileError(null);
      await queryClient.invalidateQueries({ queryKey: ['health-history', patientId] });
    },
    onError: (mutationError) => {
      setFileError(getErrorMessage(mutationError, 'Failed to delete file.'));
    },
  });

  const bmi = useMemo(() => (form ? computeBmi(form) : 'N/A'), [form]);

  if (isLoading) {
    return (
      <div className="flex justify-center p-6 py-12 max-md:p-4">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6 text-sm text-red-600 max-md:p-4">
        {getErrorMessage(error, 'Failed to load health history.')}
      </div>
    );
  }

  if (!data || !form) {
    return (
      <div className="p-6 max-md:p-4">
        <EmptyState
          title="Medical clearance unavailable"
          description="This patient does not have a medical clearance form configured yet."
        />
      </div>
    );
  }

  const applyHistoryValue = (field: MedicalClearanceField, value: string) => {
    setForm((current) => (current ? setFieldValue(current, field, value) : current));
  };

  const renderSection = (section: MedicalClearanceSection) => {
    if (section.type === 'medical_clearance_upload') {
      return (
        <UploadSection
          key={`section-${section.sectionId}`}
          patientId={patientId}
          files={data.files}
          onUpload={(file) => uploadMutation.mutate(file)}
          onDelete={(fileName) => deleteMutation.mutate(fileName)}
          section={section}
        />
      );
    }

    if (section.type === 'vital_statistics') {
      return (
        <VitalsSection
          key={`section-${section.sectionId}`}
          section={section}
          form={form}
          setForm={setForm}
          bmi={bmi}
          age={section.age ?? data.age}
          onApplyHistory={applyHistoryValue}
          showVitalsTable={showVitalsTable}
          onToggleVitalsTable={() => setShowVitalsTable((current) => !current)}
          readOnly={section.readOnly}
        />
      );
    }

    return (
      <GenericSection
        key={`section-${section.sectionId}`}
        section={section}
        form={form}
        setForm={setForm}
        onApplyHistory={applyHistoryValue}
      />
    );
  };

  return (
    <div>
      {/*
        Sticky so "Save Changes" stays reachable — this form is several screens tall and
        the footer button that used to serve that purpose is gone.

        This only works because the scroll root (PatientDetailPage, case 'health') has no
        padding of its own. Padding on the scroll container is part of the scrollable
        content, so a sticky child pins BELOW it: the first attempt lived inside
        SECTION_SHELL_CLASS and left a 24px band above the header with cards visibly
        scrolling through it. Hence px-6/pt-6 here and on the body below rather than on
        the root — the same split Appointments uses.
      */}
      <div className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-6 pt-6 pb-4 max-md:px-4 max-md:pt-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold text-slate-900">Health Information</h2>
          {data.lastUpdatedAt && (
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
              Last Updated: {formatDateTime(data.lastUpdatedAt)}
              {data.lastUpdatedBy ? ` by ${data.lastUpdatedBy}` : ''}
            </span>
          )}
        </div>
        <Button
          leftIcon={<Save className="h-4 w-4" />}
          loading={saveMutation.isPending}
          onClick={() => saveMutation.mutate(buildPayload(form))}
        >
          Save Changes
        </Button>
      </div>

      <div className="space-y-6 px-6 pt-6 pb-6 max-md:px-4">
        {saveError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {saveError}
          </div>
        )}

        {fileError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {fileError}
          </div>
        )}

        {uploadMutation.isPending && (
          <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
            Uploading file...
          </div>
        )}

        {deleteMutation.isPending && (
          <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
            Deleting file...
          </div>
        )}

        {data.sections.map(renderSection)}
      </div>
    </div>
  );
}
